
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { seedDefaultsIfEmpty } from '../services/db';
import { User } from '../shared/types';
import { useLanguage } from '../services/LanguageContext';
import { authApi } from '../services/auth';

interface LoginProps {
  onLogin: (user: User) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    seedDefaultsIfEmpty();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t('login.error.emailRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(t('login.error.invalidFormat'));
      return;
    }

    setIsLoading(true);
    try {
      const user = await authApi.login({ email: trimmedEmail, password });
      onLogin(user);
      navigate(user.role === 'admin' ? '/admin' : '/');
    } catch (err: any) {
      setError(err.message || t('login.error.invalid'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      const user = await authApi.loginWithGoogle();
      onLogin(user);
      navigate(user.role === 'admin' ? '/admin' : '/');
    } catch (err: any) {
      setError(err.message || t('login.error.invalid'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-[2.5rem] p-10 shadow-2xl">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center text-dark text-3xl mx-auto mb-6 shadow-lg shadow-emerald-500/20" aria-hidden="true">
            <i className="fas fa-lock"></i>
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{t('login.title')}</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium mt-2">{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-2xl text-sm font-bold border border-red-100 dark:border-red-900/30"
            >
              <i className="fas fa-circle-exclamation mr-2" aria-hidden="true"></i>{error}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="login-email" className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 px-1">
              {t('login.email')}
            </label>
            <div className="relative group">
              <input
                id="login-email"
                type="email"
                className="w-full h-14 pl-12 pr-4 bg-slate-50 dark:bg-dark-border/30 border-2 border-slate-300 dark:border-dark-border rounded-2xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold dark:text-white"
                placeholder="name@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <i className="fas fa-envelope absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 dark:text-emerald-500/20 group-focus-within:text-emerald-500 transition-colors" aria-hidden="true"></i>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="login-password" className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 px-1">
              {t('login.password')}
            </label>
            <div className="relative group">
              <input
                id="login-password"
                type="password"
                className="w-full h-14 pl-12 pr-4 bg-slate-50 dark:bg-dark-border/30 border-2 border-slate-300 dark:border-dark-border rounded-2xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all font-bold dark:text-white"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <i className="fas fa-shield-halved absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 dark:text-emerald-500/20 group-focus-within:text-emerald-500 transition-colors" aria-hidden="true"></i>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-emerald-500 text-dark font-black rounded-2xl hover:bg-emerald-400 transition-all shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {isLoading
              ? <i className="fas fa-spinner animate-spin" aria-hidden="true"></i>
              : <i className="fas fa-right-to-bracket" aria-hidden="true"></i>}
            {t('login.button')}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6" aria-hidden="true">
          <div className="flex-grow h-px bg-slate-100 dark:bg-dark-border"></div>
          <span className="text-xs font-black uppercase tracking-widest text-slate-300 dark:text-emerald-500/30">{t('login.or')}</span>
          <div className="flex-grow h-px bg-slate-100 dark:bg-dark-border"></div>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full py-4 bg-white dark:bg-dark-border/30 border-2 border-slate-300 dark:border-dark-border text-slate-700 dark:text-white font-black rounded-2xl hover:border-emerald-500 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
        >
          <i className="fab fa-google text-emerald-500" aria-hidden="true"></i>
          {t('login.google')}
        </button>

        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-emerald-500 hover:text-emerald-400 transition-colors">
              {t('login.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
